import { gql } from 'graphql-request'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Plus } from 'lucide-react'

import { useMutation } from '@hooks/graphql'
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Label } from "@/components/ui/label"
import CounterInput from '@/components/ui/CounterInput'
import TextEditor from '@components/TextEditor'
import SaveButton from './SaveButton'

const UPDATE_QUESTION_BLOCK_MUTATION = gql`
  mutation updateQuestionBlock($input: UpdateQuestionBlockInput!) {
    updateQuestionBlock(input: $input) {
      question {
        label
        markAllocation
        options {
          label
          correct
        }
      }
      errors {
        message
      }
    }
  }
`

const formSchema = z.object({
  label: z.string().min(1, "Question text is required."),
  markAllocation: z.number().min(1, "Mark allocation must be at least 1."),
  options: z.array(z.object({
    label: z.string().min(1, "Option text cannot be empty."),
    correct: z.boolean()
  })).min(1, "At least one option is required.")
})

const MultipleChoiceQuestionForm = ({ id, label, markAllocation, options, refetch, uploadImage }) => {
  const [saving, setSaving] = useState(false)

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      markAllocation: markAllocation || 1,
      label: label || '',
      options: options || [{ label: '', correct: false }]
    }
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'options'
  })

  const { mutate: updateQuestionBlock, isLoading } = useMutation({
    gqlMutation: UPDATE_QUESTION_BLOCK_MUTATION,
    onSuccess: (data) => {
      refetch()
      setSaving(false)
      form.reset({
        label: data?.updateQuestionBlock?.question?.label || label,
        markAllocation: data?.updateQuestionBlock?.question?.markAllocation || markAllocation,
        options: data?.updateQuestionBlock?.question?.options || options
      })
    },
    onError: () => {
      setSaving(false);
    }
  })

  function onSubmit(values) {
    setSaving(true)
    updateQuestionBlock({ input: { id, ...values } })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='w-full flex flex-col gap-5'>
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Question</FormLabel>
              <FormControl>
                <TextEditor.Container>
                  <Controller
                      name='label'
                      control={form.control}
                      render={({ field: controllerField }) => (
                          <TextEditor
                              uploadImage={uploadImage}
                              id='label'
                              {...controllerField}
                          />
                      )}
                  />
                  <TextEditor.Actions>
                      <TextEditor.ToolbarToggle />
                  </TextEditor.Actions>
                </TextEditor.Container>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="markAllocation"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor='mark-allocation'>Mark allocation</FormLabel>
              <FormDescription>
                The maximum marks allocated for this question.
              </FormDescription>
              <FormControl>
                <CounterInput
                  id='mark-allocation'
                  control={form.control}
                  name={field.name}
                  rules={{ min: { value: 1, message: 'Mark allocation must be greater than 0' } }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <Label className='mb-2 block'>Answers</Label>

          {fields.map((item, index) => (
            <div key={item.id} className='w-full flex flex-row items-start mb-3 gap-2'>
               <FormField
                  control={form.control}
                  name={`options.${index}.correct`}
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 pt-1.5">
                       <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          aria-label='Correct answer'
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

               <div className='grow'>
                <FormField
                  control={form.control}
                  name={`options.${index}.label`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                         <TextEditor.Container toolbarEnabled={false} className='grow'>
                            <Controller
                                name={`options.${index}.label`}
                                control={form.control}
                                render={({ field: controllerField }) => (
                                    <TextEditor
                                        uploadImage={uploadImage}
                                        {...controllerField}
                                    />
                                )}
                            />
                            <TextEditor.Actions>
                                <TextEditor.ToolbarToggle />
                            </TextEditor.Actions>
                        </TextEditor.Container>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  />
               </div>

              <Button
                aria-label='Remove answer'
                type='button'
                variant='ghost'
                size='icon'
                onClick={() => remove(index)}
                className='ml-1 self-center'
              >
                <TrashIcon className='h-5 w-5' />
              </Button>
            </div>
          ))}

          <Button
         variant="outline"
         className='mt-2 border-dashed text-gray-500'
            onClick={() => append({ label: '', correct: false })}
          >
                <PlusIcon className='size-5' />
            Add answer
          </Button>

 
           <FormField
              control={form.control}
              name="options"
              render={() => <FormMessage />}
            />
        </div>

        <div className='flex justify-end mt-3'>
          <SaveButton
            saving={saving}
            hasChanges={form.formState.isDirty}
            isLoading={isLoading}
            type="submit"
          />
        </div>
      </form>
    </Form>
  )
}

export default MultipleChoiceQuestionForm
